import { useEffect, useState } from "react";
import { useParams, Link } from "react-router";
import { getBusiness } from "../../api/business.api";
import { getServices } from "../../api/service.api";
import { getBusinessPosts } from "../../api/post.api";
import { BusinessHeader } from "../../components/business/BusinessHeader";
import { ServiceCard } from "../../components/booking/ServiceCard";
import { PostGrid } from "../../components/post/PostGrid";
import { Skeleton, SkeletonText, SkeletonCircle } from "../../components/ui/Skeleton";
import { useAuthStore } from "../../stores/auth.store";
import type { BusinessProfile, Service, Post, PostImage } from "@abejon/shared";

export function ProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const user = useAuthStore((s) => s.user);
  const [business, setBusiness] = useState<(BusinessProfile & { ownerName: string }) | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [postsList, setPostsList] = useState<(Post & { images: PostImage[] })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loadKey, setLoadKey] = useState(0);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError("");

    async function load() {
      try {
        const data = await getBusiness(slug!);
        if (!cancelled) {
          setBusiness(data.business);
          // Fetch services and posts
          try {
            const [servicesData, postsData] = await Promise.all([
              getServices(data.business.id),
              getBusinessPosts(slug!),
            ]);
            if (!cancelled) {
              setServices(servicesData.services);
              setPostsList(postsData.posts);
            }
          } catch {
            // Non-critical
          }
        }
      } catch {
        if (!cancelled) setError("Business not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug, loadKey]);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Cover */}
        <Skeleton className="h-40 w-full rounded-xl sm:h-52" />
        {/* Avatar + name */}
        <div className="px-4">
          <div className="-mt-12 mb-3">
            <SkeletonCircle className="h-24 w-24 border-4 border-white" />
          </div>
          <SkeletonText className="h-6 w-48" />
          <SkeletonText className="mt-2 h-4 w-32" />
          <SkeletonText className="mt-3 h-4 w-full" />
        </div>
        {/* Action buttons */}
        <div className="flex gap-3 px-4">
          <Skeleton className="h-10 w-28 rounded-lg" />
          <Skeleton className="h-10 w-28 rounded-lg" />
        </div>
        {/* Service cards */}
        <div className="space-y-3 px-4">
          <SkeletonText className="h-5 w-24" />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="py-16 text-center">
        <h1 className="mb-2 text-xl font-bold text-gray-300">Business not found</h1>
        <p className="mb-4 text-sm text-gray-500">Failed to load this profile.</p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => setLoadKey((k) => k + 1)}
            className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
          >
            Retry
          </button>
          <Link to="/explore" className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Back to explore
          </Link>
        </div>
      </div>
    );
  }

  const isOwner = user?.id === business.userId;

  return (
    <div className="space-y-6">
      <BusinessHeader
        displayName={business.displayName}
        category={business.category}
        bio={business.bio}
        avatarUrl={business.avatarUrl}
        coverUrl={business.coverUrl}
        city={business.city}
        state={business.state}
      />

      <div className="flex gap-3 px-4">
        <Link
          to={`/book/${business.slug}`}
          className="rounded-lg bg-gray-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-gray-800"
        >
          Book Now
        </Link>
        {user && !isOwner && (
          <Link
            to={`/messages/new?businessId=${business.id}&businessName=${encodeURIComponent(business.displayName)}`}
            className="rounded-lg border border-gray-300 px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Message
          </Link>
        )}
        {isOwner && (
          <Link
            to="/dashboard/settings"
            className="rounded-lg border border-gray-300 px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Edit Profile
          </Link>
        )}
      </div>

      {/* Services */}
      {services.length > 0 && (
        <div className="px-4">
          <h2 className="mb-3 text-lg font-semibold text-gray-900">Services</h2>
          <div className="space-y-3">
            {services.map((s) => (
              <ServiceCard key={s.id} service={s} />
            ))}
          </div>
        </div>
      )}

      {/* Posts */}
      {postsList.length > 0 && (
        <div className="px-4">
          <h2 className="mb-3 text-lg font-semibold text-gray-900">Posts</h2>
          <PostGrid posts={postsList} />
        </div>
      )}
    </div>
  );
}
